import Box from "@mui/material/Box";
import { grey, lime } from "@mui/material/colors";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { GlobalState } from "context/GlobalContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const defaultLimit = 20;

const sameAudiencePage = (prev, next) => {
	if (!Array.isArray(prev) || prev.length !== next.length) return false;
	for (let i = 0; i < next.length; i += 1) {
		const a = prev[i];
		const b = next[i];
		if (
			a?.id !== b?.id ||
			a?.updated_at !== b?.updated_at ||
			a?.message !== b?.message ||
			a?.is_active !== b?.is_active ||
			a?.row_count !== b?.row_count
		) {
			return false;
		}
	}
	return true;
};

const DatagridWithApiPagination = ({
	tableRows = [],
	setTableRows,
	fetchTableRows = () => ({ result: [], totalRows: defaultLimit }),
	columns = [],
	handleRowClick,
	getRowId,
	recordsLimit = defaultLimit,polling = false, pollingTime = 60000,
	gridSx,
	rowHeight = 40,
	getRowHeight,
	disableVirtualization = false,
	columnHeaderHeight = 35,
	reloadKey = "",
	allowEmpty = false,
	serverSort = false,
	sortModel,
	onSortModelChange,
	pageSizeOptions,
	onPageSizeChange,
	loadAll = false,
	onLeaveLoadAll,
}) => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(recordsLimit);
	const [loading, setLoading] = useState(false);
	const [totalRecorsCount, setTotalRecordsCount] = useState(recordsLimit);
	const reloadKeyRef = useRef(reloadKey);
	const hasRowsRef = useRef(false);

	const fetchDataBasedonPage = async (page, pageSize, { silent = false, all = false } = {}) => {
		const showLoader = !silent && !hasRowsRef.current;
		try {
			if (showLoader) setLoading(true);
			const limit = all ? 100000 : pageSize;
			const offset = all ? 0 : page * pageSize;
			const { result, total_rows: totalRows } = (await fetchTableRows(limit, offset)) || {};
			if (result?.length) {
				hasRowsRef.current = true;
				setTableRows((prev) => (silent && sameAudiencePage(prev, result) ? prev : result));
				setTotalRecordsCount((count) => {
					const next = totalRows ? totalRows : recordsLimit;
					return count === next ? count : next;
				});
			} else if (allowEmpty && Array.isArray(result)) {
				hasRowsRef.current = true;
				setTableRows([]);
				setTotalRecordsCount(Number(totalRows) || 0);
			} else throw new Error("Fetched 0 Table Rows");
		} catch (err) {
			console.error("Failed to fetch styles:", err);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrMsgFromError(err) });
		} finally {
			if (showLoader) setLoading(false);
		}
	};

	useEffect(() => {
		if (reloadKeyRef.current !== reloadKey) {
			reloadKeyRef.current = reloadKey;
			if (page !== 0) {
				setPage(0);
				return;
			}
		}
		if (loadAll && page !== 0) {
			setPage(0);
			return;
		}
		fetchDataBasedonPage(page, pageSize, { all: loadAll });
		if (polling) {
			const interval = setInterval(() => {fetchDataBasedonPage(page, pageSize, { silent: true, all: loadAll })}, pollingTime);
	
			// Cleanup on component unmount
			return () => clearInterval(interval);
			// Set interval to call every 60 seconds
		}
	}, [page, pageSize, reloadKey, loadAll]);

	const gridStyle = useMemo(
		() => ({
			width: "100%",
			height: "100%",
			border: "none",
			"& .MuiDataGrid-row": {
				cursor: "pointer",
				border: "none",
			},
			"& .MuiDataGrid-cell": {
				padding: 0,
			},
			"& .MuiDataGrid-columnHeader": {
				cursor: "pointer",
				background: grey[300],
			},
			...(gridSx || {}),
		}),
		[gridSx]
	);

	return (
		<Stack spacing={2} margin="auto" width="100%" height="100%">
			<DataGrid
				rows={tableRows?.length ? tableRows : tableRows}
				// getRowId={(row) => row?.SITECODE}
				columns={columns}
				sx={gridStyle}
				getRowId={(row) => (typeof getRowId === "function" ? getRowId(row) : row.id)}
				columnHeaderHeight={columnHeaderHeight}
				rowHeight={getRowHeight ? undefined : rowHeight}
				getRowHeight={getRowHeight}
				disableVirtualization={disableVirtualization}
				disableRowSelectionOnClick
				disableColumnMenu
				disableColumnFilter
				disableColumnSelector
				rowCount={totalRecorsCount}
				loading={loading}
				paginationMode="server"
				sortingMode={serverSort ? "server" : "client"}
				sortModel={serverSort ? sortModel || [] : undefined}
				onSortModelChange={serverSort ? onSortModelChange : undefined}
				pageSizeOptions={pageSizeOptions}
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={({ page: nextPage, pageSize: nextPageSize }) => {
					if (loadAll) onLeaveLoadAll?.();
					if (nextPageSize !== pageSize) onPageSizeChange?.(nextPageSize);
					setPage(nextPageSize !== pageSize ? 0 : nextPage);
					setPageSize(nextPageSize);
				}}
				onRowClick={({ row }) => typeof handleRowClick === "function" && handleRowClick(row)}
			/>
			{/* {loading && (
				<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
					<Typography variant="body2" color={grey[900]}>
						Loading...{" "}
					</Typography>
					<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
				</Box>
			)} */}
		</Stack>
	);
};

export default DatagridWithApiPagination;
